import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../config/api';
import {
  FiMail,
  FiSave,
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
  FiPlus,
  FiTrash2,
} from 'react-icons/fi';
import LoadingButton from '../LoadingButton';
import SupportEmailConfig from './SupportEmailConfig';
import { appAlert, appConfirm } from '../../utils/appDialog';

const SECURE_OPTIONS = [
  { value: 'starttls', label: 'STARTTLS (port 587)' },
  { value: 'ssl', label: 'SSL/TLS (port 465)' },
  { value: 'off', label: 'None (port 25)' },
];

const CUSTOM_ACTION_VALUE = '__custom__';

function slugify(label) {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
}

function newTemplateId() {
  return `tpl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function templateBodyFrom(tpl = {}) {
  if (typeof tpl.body === 'string' && tpl.body) return tpl.body;
  if (typeof tpl.body_html === 'string' && tpl.body_html) return tpl.body_html;
  if (typeof tpl.body_text === 'string' && tpl.body_text) return tpl.body_text;
  return '';
}

const EmailConfig = () => {
  const queryClient = useQueryClient();
  const [expandedAction, setExpandedAction] = useState('user_signup');
  const [expandedCustom, setExpandedCustom] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    app_name: 'GoBunnyy',
    smtp: {
      host: '',
      port: 587,
      secure: 'starttls',
      username: '',
      password: '',
      from_name: '',
      from_email: '',
    },
    actions: {},
    templates: {},
    custom_templates: [],
    custom_actions: [],
  });

  const { data, isLoading, isError, error } = useQuery(
    ['settings-email'],
    () => api.get('/permissions/email').then((r) => r.data),
    { refetchOnWindowFocus: false, retry: false }
  );

  useEffect(() => {
    if (!data) return;
    const templates = { ...(data.templates || {}) };
    for (const [id, tpl] of Object.entries(templates)) {
      templates[id] = {
        ...tpl,
        body: templateBodyFrom(tpl),
      };
    }
    setForm((prev) => ({
      ...prev,
      enabled: !!data.enabled,
      app_name: data.app_name || 'GoBunnyy',
      smtp: {
        ...prev.smtp,
        host: data.smtp?.host || '',
        port: data.smtp?.port ?? 587,
        secure: data.smtp?.secure || 'starttls',
        username: data.smtp?.username || '',
        password: '',
        from_name: data.smtp?.from_name || '',
        from_email: data.smtp?.from_email || '',
      },
      actions: { ...(data.actions || {}) },
      templates,
      custom_templates: (data.custom_templates || []).map((t) => ({
        ...t,
        body: templateBodyFrom(t),
        action_mode: t.is_custom_action ? CUSTOM_ACTION_VALUE : t.action_id,
        custom_action_label: t.is_custom_action ? t.action_id : '',
      })),
      custom_actions: data.custom_actions || [],
    }));
  }, [data]);

  const saveMutation = useMutation(
    (body) => api.put('/permissions/email', body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['settings-email']);
        queryClient.invalidateQueries(['email-queue-status']);
        void appAlert('Email configuration saved.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const resetTemplateMutation = useMutation(
    (actionId) => api.post(`/permissions/email/templates/${actionId}/reset`),
    {
      onSuccess: (res, actionId) => {
        const tpl = res.data.template || {};
        setForm((prev) => ({
          ...prev,
          templates: {
            ...prev.templates,
            [actionId]: {
              ...tpl,
              body: templateBodyFrom(tpl),
            },
          },
        }));
        queryClient.invalidateQueries(['settings-email']);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const systemCatalog = (data?.action_catalog || []).filter((a) => a.system !== false);
  const systemActions =
    systemCatalog.length > 0
      ? systemCatalog
      : (data?.action_catalog || []).filter((a) => !String(a.id || '').startsWith('custom'));
  const systemActionIds = new Set(systemActions.map((a) => a.id));

  const renderSystemAction = (action) => {
    const isOpen = expandedAction === action.id;
    const tpl = form.templates[action.id] || {};
    const override = (form.custom_templates || []).find(
      (t) =>
        t.enabled !== false &&
        (t.action_mode === action.id ||
          (!t.is_custom_action && t.action_id === action.id))
    );
    return (
      <div key={action.id} className="email-action-card">
        <div className="email-action-header">
          <label className="email-action-toggle">
            <input
              type="checkbox"
              checked={form.actions[action.id] !== false}
              onChange={(e) => toggleAction(action.id, e.target.checked)}
            />
            <span className="email-action-title">{action.label}</span>
          </label>
          <button
            type="button"
            className="btn btn-link email-expand-btn"
            onClick={() => setExpandedAction(isOpen ? null : action.id)}
            aria-expanded={isOpen}
          >
            Template {isOpen ? <FiChevronUp /> : <FiChevronDown />}
          </button>
        </div>
        <p className="email-action-desc">{action.description}</p>
        {override && (
          <p className="email-vars-hint">
            Override active: custom template “{override.label}” is bound to this action.
          </p>
        )}

        {isOpen && (
          <div className="email-template-editor">
            <p className="email-vars-hint">
              Variables:{' '}
              {(action.variables || []).map((v) => (
                <code key={v}>{'{{' + v + '}}'}</code>
              ))}
            </p>
            <div className="form-group">
              <label>Subject</label>
              <input
                type="text"
                value={tpl.subject || ''}
                onChange={(e) => updateTemplate(action.id, 'subject', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Body (plain text or HTML)</label>
              <textarea
                rows={8}
                value={templateBodyFrom(tpl)}
                onChange={(e) => updateTemplate(action.id, 'body', e.target.value)}
                className="email-html-textarea"
                placeholder={'Hi {{first_name}},\n\n… or <p>Hi {{first_name}}</p>'}
              />
              <small>
                HTML is used when the body contains tags; otherwise plain text is converted
                to a simple HTML alternative automatically.
              </small>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={async () => {
                if (await appConfirm(`Reset "${action.label}" template to default?`)) {
                  resetTemplateMutation.mutate(action.id);
                }
              }}
              disabled={resetTemplateMutation.isLoading}
            >
              <FiRefreshCw /> Reset to default
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleSave = (e) => {
    e.preventDefault();
    const templates = {};
    for (const [id, tpl] of Object.entries(form.templates || {})) {
      templates[id] = {
        subject: tpl.subject || '',
        body: typeof tpl.body === 'string' ? tpl.body : templateBodyFrom(tpl),
      };
    }

    const custom_templates = (form.custom_templates || []).map((t) => {
      const isCustomMode =
        t.action_mode === CUSTOM_ACTION_VALUE ||
        (t.is_custom_action && !t.action_mode);
      let action_id = isCustomMode
        ? slugify(t.custom_action_label || t.action_id || t.label)
        : t.action_mode || t.action_id;
      if (!action_id) action_id = slugify(t.label) || newTemplateId();
      const is_custom_action = isCustomMode || !systemActionIds.has(action_id);
      return {
        id: t.id || newTemplateId(),
        label: (t.label || action_id).trim() || action_id,
        description: t.description || '',
        action_id,
        is_custom_action,
        variables: Array.isArray(t.variables) && t.variables_text == null
          ? t.variables
          : String(t.variables_text != null ? t.variables_text : (t.variables || []).join(', '))
              .split(/[,\s]+/)
              .map((v) => v.trim())
              .filter(Boolean),
        subject: t.subject || '',
        body: typeof t.body === 'string' ? t.body : templateBodyFrom(t),
        enabled: t.enabled !== false,
        manual: t.manual !== false,
      };
    });

    const body = {
      enabled: form.enabled,
      app_name: form.app_name,
      smtp: { ...form.smtp },
      actions: form.actions,
      templates,
      custom_templates,
    };
    if (!body.smtp.password?.trim()) delete body.smtp.password;
    saveMutation.mutate(body);
  };

  const updateTemplate = (actionId, field, value) => {
    setForm((prev) => ({
      ...prev,
      templates: {
        ...prev.templates,
        [actionId]: {
          ...(prev.templates[actionId] || {}),
          [field]: value,
        },
      },
    }));
  };

  const toggleAction = (actionId, enabled) => {
    setForm((prev) => ({
      ...prev,
      actions: { ...prev.actions, [actionId]: enabled },
    }));
  };

  const updateCustomTemplate = (index, field, value) => {
    setForm((prev) => {
      const next = [...(prev.custom_templates || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, custom_templates: next };
    });
  };

  const addCustomTemplate = () => {
    const id = newTemplateId();
    setForm((prev) => ({
      ...prev,
      custom_templates: [
        ...(prev.custom_templates || []),
        {
          id,
          label: 'New custom template',
          description: '',
          action_mode: CUSTOM_ACTION_VALUE,
          custom_action_label: '',
          action_id: '',
          is_custom_action: true,
          variables: ['first_name', 'last_name', 'email', 'app_name'],
          variables_text: 'first_name, last_name, email, app_name',
          subject: 'Hello {{first_name}}',
          body: 'Hi {{first_name}},\n\n…\n\n— {{app_name}}',
          enabled: true,
          manual: true,
        },
      ],
    }));
    setExpandedCustom(id);
  };

  const removeCustomTemplate = async (index) => {
    const tpl = form.custom_templates[index];
    if (!(await appConfirm(`Delete template "${tpl?.label || 'custom'}"?`))) return;
    setForm((prev) => ({
      ...prev,
      custom_templates: prev.custom_templates.filter((_, i) => i !== index),
    }));
  };

  if (isLoading) return <div className="loading">Loading email configuration…</div>;

  if (isError) {
    const status = error?.response?.status;
    const msg = error?.response?.data?.error || error?.message;
    return (
      <div className="settings-section email-settings">
        <h2><FiMail /> Email configuration</h2>
        <div className="error" style={{ marginTop: 16 }}>
          <p><strong>Could not load email settings{status ? ` (${status})` : ''}.</strong></p>
          <p>{msg || 'The API endpoint may not be deployed yet.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section email-settings">
      <h2>
        <FiMail /> Email configuration
      </h2>
      <p className="email-settings-intro">
        Configure outbound email via SMTP. Choose which events send mail and customize templates
        with <code>{'{{variable}}'}</code> placeholders. Use a single body field for plain text or
        HTML. Add custom templates bound to a system event or your own action key for canned sends.
        Credentials are stored encrypted. Delivery history:{' '}
        <Link to="/email-queue" className="email-inline-link">
          Email queue <FiExternalLink aria-hidden />
        </Link>
        .
      </p>

      <form onSubmit={handleSave} className="settings-form">
        <div className="form-group">
          <label className="email-toggle-label">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            {' '}Enable outbound email
          </label>
          <small>When off, no transactional emails are sent regardless of per-action toggles.</small>
        </div>

        <div className="form-group">
          <label>Application name (used in templates)</label>
          <input
            type="text"
            value={form.app_name}
            onChange={(e) => setForm({ ...form, app_name: e.target.value })}
            placeholder="GoBunnyy"
          />
        </div>

        <h3 className="email-section-heading">SMTP server</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Host</label>
            <input
              type="text"
              value={form.smtp.host}
              onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, host: e.target.value } })}
              placeholder="smtp.example.com"
            />
          </div>
          <div className="form-group">
            <label>Port</label>
            <input
              type="number"
              min={1}
              max={65535}
              value={form.smtp.port}
              onChange={(e) =>
                setForm({ ...form, smtp: { ...form.smtp, port: parseInt(e.target.value, 10) || 587 } })
              }
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Security</label>
            <select
              value={form.smtp.secure}
              onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, secure: e.target.value } })}
            >
              {SECURE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              autoComplete="off"
              value={form.smtp.username}
              onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, username: e.target.value } })}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              Password
              {data?.smtp?.password_set && (
                <span className="email-key-hint"> (saved — leave blank to keep)</span>
              )}
            </label>
            <input
              type="password"
              autoComplete="new-password"
              value={form.smtp.password}
              onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, password: e.target.value } })}
              placeholder={data?.smtp?.password_set ? '••••••••' : 'SMTP password'}
            />
          </div>
          <div className="form-group">
            <label>From email</label>
            <input
              type="email"
              value={form.smtp.from_email}
              onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, from_email: e.target.value } })}
              placeholder="noreply@yourcompany.com"
            />
          </div>
        </div>

        <div className="form-group">
          <label>From display name</label>
          <input
            type="text"
            value={form.smtp.from_name}
            onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, from_name: e.target.value } })}
            placeholder="GoBunnyy"
          />
        </div>

        <h3 className="email-section-heading">System notification actions</h3>
        <p className="email-actions-hint">
          Toggle which product events trigger email. Each action has a subject and a single body
          (plain text or HTML). An enabled custom template bound to the same action overrides this
          body when mail is sent.
        </p>

        {(data?.action_categories || []).length
          ? (data.action_categories || []).map((cat) => {
              const grouped = systemActions.filter((a) => (a.category || 'other') === cat.id);
              if (!grouped.length) return null;
              return (
                <div key={cat.id} className="email-action-group">
                  <h4 className="email-action-group-title">{cat.label}</h4>
                  <div className="email-actions-list">{grouped.map(renderSystemAction)}</div>
                </div>
              );
            })
          : (
            <div className="email-actions-list">{systemActions.map(renderSystemAction)}</div>
          )}
        {(() => {
          const cats = data?.action_categories || [];
          if (!cats.length) return null;
          const known = new Set(cats.map((c) => c.id));
          const leftovers = systemActions.filter((a) => !a.category || !known.has(a.category));
          if (!leftovers.length) return null;
          return (
            <div className="email-action-group">
              <h4 className="email-action-group-title">Other</h4>
              <div className="email-actions-list">{leftovers.map(renderSystemAction)}</div>
            </div>
          );
        })()}

        <h3 className="email-section-heading">Custom templates</h3>
        <p className="email-actions-hint">
          Create your own templates and bind them to a <strong>system action</strong> (overrides
          that event’s body) or a <strong>custom action</strong> (for canned / manual sends under a
          name you choose).
        </p>

        <div className="form-actions" style={{ marginBottom: 12 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={addCustomTemplate}>
            <FiPlus /> Add custom template
          </button>
        </div>

        <div className="email-actions-list">
          {(form.custom_templates || []).map((tpl, index) => {
            const isOpen = expandedCustom === tpl.id;
            const actionMode =
              tpl.action_mode ||
              (tpl.is_custom_action ? CUSTOM_ACTION_VALUE : tpl.action_id);
            return (
              <div key={tpl.id || index} className="email-action-card">
                <div className="email-action-header">
                  <label className="email-action-toggle">
                    <input
                      type="checkbox"
                      checked={tpl.enabled !== false}
                      onChange={(e) => updateCustomTemplate(index, 'enabled', e.target.checked)}
                    />
                    <span className="email-action-title">{tpl.label || 'Custom template'}</span>
                  </label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-link email-expand-btn"
                      onClick={() => setExpandedCustom(isOpen ? null : tpl.id)}
                    >
                      Edit {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                    </button>
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => removeCustomTemplate(index)}
                      title="Delete template"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="email-template-editor">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Template name</label>
                        <input
                          type="text"
                          value={tpl.label || ''}
                          onChange={(e) => updateCustomTemplate(index, 'label', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Action</label>
                        <select
                          value={actionMode}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateCustomTemplate(index, 'action_mode', v);
                            updateCustomTemplate(
                              index,
                              'is_custom_action',
                              v === CUSTOM_ACTION_VALUE
                            );
                          }}
                        >
                          <option value={CUSTOM_ACTION_VALUE}>Custom action…</option>
                          {systemActions.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.label} ({a.id})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {actionMode === CUSTOM_ACTION_VALUE && (
                      <div className="form-group">
                        <label>Custom action name / key</label>
                        <input
                          type="text"
                          value={tpl.custom_action_label || ''}
                          onChange={(e) =>
                            updateCustomTemplate(index, 'custom_action_label', e.target.value)
                          }
                          placeholder="e.g. nurture_follow_up"
                        />
                        <small>
                          Saved as key:{' '}
                          <code>
                            {slugify(tpl.custom_action_label || tpl.label) || 'custom_…'}
                          </code>
                          . Use this when sending canned mail for your own campaigns.
                        </small>
                      </div>
                    )}

                    <div className="form-group">
                      <label>Description (optional)</label>
                      <input
                        type="text"
                        value={tpl.description || ''}
                        onChange={(e) => updateCustomTemplate(index, 'description', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>Variables (comma-separated)</label>
                      <input
                        type="text"
                        value={
                          tpl.variables_text != null
                            ? tpl.variables_text
                            : (tpl.variables || []).join(', ')
                        }
                        onChange={(e) =>
                          updateCustomTemplate(index, 'variables_text', e.target.value)
                        }
                        placeholder="first_name, email, app_name"
                      />
                    </div>

                    <label className="email-toggle-label">
                      <input
                        type="checkbox"
                        checked={tpl.manual !== false}
                        onChange={(e) => updateCustomTemplate(index, 'manual', e.target.checked)}
                      />
                      {' '}Available for manual / canned send
                    </label>

                    <div className="form-group">
                      <label>Subject</label>
                      <input
                        type="text"
                        value={tpl.subject || ''}
                        onChange={(e) => updateCustomTemplate(index, 'subject', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Body (plain text or HTML)</label>
                      <textarea
                        rows={8}
                        value={templateBodyFrom(tpl)}
                        onChange={(e) => updateCustomTemplate(index, 'body', e.target.value)}
                        className="email-html-textarea"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {(form.custom_templates || []).length === 0 && (
            <p className="email-actions-hint">No custom templates yet.</p>
          )}
        </div>

        <div className="form-actions">
          <LoadingButton
            type="submit"
            className="btn btn-primary"
            icon={FiSave}
            loading={saveMutation.isLoading}
            loadingLabel="Saving…"
          >
            Save configuration
          </LoadingButton>
        </div>
      </form>

      <SupportEmailConfig />
    </div>
  );
};

export default EmailConfig;
